import React, { useEffect, useRef, useState } from 'react';
import { FiX } from 'react-icons/fi';
import pkg from '../package.json';
import './HelpModal.css';

export default function HelpModal({ onClose, animationsEnabled, onToggleAnimations }) {
    const modalRef = useRef(null);
    const [exiting, setExiting] = useState(false);

    const requestClose = () => {
        if (exiting) return; // prevent double trigger
        setExiting(true);
        // Wait for CSS animation (250ms) then actually unmount
        setTimeout(() => {
            onClose();
        }, 250);
    };

    // Close on outside click after mount delay (mirror details panel behavior)
    useEffect(() => {
        const handleOutside = (e) => {
            if (modalRef.current && !modalRef.current.contains(e.target)) {
                requestClose();
            }
        };
        const timer = setTimeout(() => {
            document.addEventListener('mousedown', handleOutside);
        }, 100);
        return () => {
            clearTimeout(timer);
            document.removeEventListener('mousedown', handleOutside);
        };
    }, [onClose]);

    // Close on Escape
    useEffect(() => {
        const handleEsc = (e) => { if (e.key === 'Escape') requestClose(); };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [onClose]);

    return (
        <div className={`help-overlay ${exiting ? 'exiting' : ''}`.trim()} role="dialog" aria-modal="true">
            <div className={`help-modal ${exiting ? 'exiting' : ''}`.trim()} ref={modalRef}>
                <div className="help-header">
                    <div className="help-title-group">
                        <h2>Dashboard Help</h2>
                        <span className="info-value" style={{ fontSize: '0.75rem', opacity: 0.7 }}>
                            <a
                                href={`https://github.com/oriinc/system-state-dashboard`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {"system-state-dashboard v" + pkg.version}
                            </a>
                        </span>
                    </div>
                    <button className="help-close" onClick={requestClose} title="Close (Esc)"><FiX /></button>
                </div>
                <div className="help-content">
                    <section>
                        <h3>1. Connecting & Subscribing</h3>
                        <ul>
                            <li>If you know the device IDs youre interested in monitoring, enter them in the input field separated by commas.</li>
                            <li>This allows you to focus on specific devices without subscribing to all devices.</li>
                            <li>If instead you want to monitor all devices, tick the <code>Subscribe to all devices</code> checkbox.</li>
                            <li>Checking <code>Initialize all device states</code> causes an initial snapshot of device states to load.</li>
                            <li>Once you've determined which devices to subscribe to, pressing <strong>Subscribe</strong> will cause all future events from the subscribed devices to be sent to your dashboard.</li>
                            <li><strong>Unsubscribe</strong> tells the server to no longer send you updated for the previously subscribed to devices.</li>
                        </ul>
                    </section>
                    <section>
                        <h3>2. Filters & Sorting</h3>
                        <ul>
                            <li>Search matches against device ID and resolved system type name.</li>
                            <li>System Type & Firmware filters accept one or more entries</li>
                            <li>Sorting options prioritize the selected field; ties break by device ID for deterministic ordering.</li>
                        </ul>
                    </section>
                    <section>
                        <h3>3. Device Cards</h3>
                        <ul>
                            <li>Status indicator shows online/offline.</li>
                            <li>Movement section shows current position and active movement (if any).</li>
                            <li>
                                Click a card to open details in the side panel. The device details panel adds sections for <b>Salesforce Data</b> (if available) and <b>Aggregated Data</b>, which lists the most data for any received event.
                                For example, an online system would have the most recent <code>k/status</code> event as <code>online</code>, rather than <code>offline</code>. Movement data for the last month and last day is also displayed, if known.
                            </li>
                        </ul>
                    </section>
                    <div className="help-footer-note">
                        Turning off animations will improve performance, especially with all devices subscribed.
                    </div>
                    <div className="help-footer-actions">
                        <button
                            type="button"
                            className={`btn ${animationsEnabled ? '' : 'btn-secondary'}`.trim()}
                            onClick={onToggleAnimations}
                        >
                            {animationsEnabled ? 'Animations enabled' : 'Animations disabled'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
